import type { MenuProps } from "antd";

import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";

/**
 * PROFILE_SIMPLIFIED (Takeda) is the third, orthogonal treatment: the menu is
 * cut to the surfaces the tenant actually works in and reordered so the
 * site's own performance leads. Forms and Monitoring Visits — placeholders for
 * every tenant, and trial-scoped questions rather than cross-trial ones — are
 * dropped, Notes follows NOTES_HIDDEN, and the reading order becomes
 * Performance (injected by the profile) → Milestones → Surveys → Trials →
 * Data Mastery. It composes with the two flags below only in the sense that a
 * simplified tenant is not expected to carry them.
 *
 * Tab filtering is opt-in via SITE_TAB_GATING (Florence): with the flag on,
 * entries whose backing data the tenant does not have are omitted entirely —
 * a menu item that cannot be opened, or opens on an empty screen, should
 * leave no trace rather than advertise itself greyed out. Tenants without
 * either flag (clario, demo) keep the full menu, including the disabled
 * Forms / Monitoring Visits placeholders, exactly as before.
 *
 * Backing-data mapping under gating: milestones -> site-activation-milestone
 * (the site_study_link/milestone_timeseries feed, mirroring the trial-level
 * site profile); surveys -> none (the Alchemer site_profile feed has no flag
 * yet, and no gated tenant carries it), so it is simply omitted; forms and
 * monitoring-visits are placeholder screens for every tenant, so under
 * gating they are omitted too.
 *
 * The RISK group is omitted entirely without SITE_RISK_LIFECYCLE rather than
 * shown disabled: a tenant with no scoring data should see no trace of the
 * section, not a greyed-out entry advertising it.
 *
 * SITE_TAB_DISABLED (clario) is the softer, independent alternative to gating:
 * milestones and surveys stay listed but greyed out. Clario's feed populates
 * neither, yet the tenant is not opted into the omit-entirely treatment, so the
 * entries remain visible as "nothing here for you" rather than disappearing.
 * The two flags compose: gating wins, because an omitted tab cannot be greyed.
 */
export const createCrossTrialSiteMenuItems = (
  trialCount?: number,
  isFlagEnabled: (flag: string) => boolean = () => false,
): MenuProps["items"] => {
  const gated = isFlagEnabled(FEATURE_FLAGS.SITE_TAB_GATING);
  const noData = isFlagEnabled(FEATURE_FLAGS.SITE_TAB_DISABLED);
  const simplified = isFlagEnabled(FEATURE_FLAGS.PROFILE_SIMPLIFIED);
  const notesHidden = isFlagEnabled(FEATURE_FLAGS.NOTES_HIDDEN);

  const trialsItem = {
    key: "performance",
    label:
      trialCount != null ? (
        <span className="flex items-center justify-between">
          Trials
          <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] rounded-full bg-[#E8EEF2] px-1.5 text-[11px] font-semibold text-[#48636F]">
            {trialCount}
          </span>
        </span>
      ) : (
        "Trials"
      ),
  };

  if (simplified) {
    // Performance is injected by the profile (withPerformanceTab) after this
    // builder returns, anchored on the Trials entry. Under simplification it
    // must lead instead, so the anchor moves to the front of the group and the
    // trial list follows the two dated surfaces.
    return [
      {
        type: "group",
        label: "SITE OVERVIEW",
        key: "group-site-overview",
        children: [
          { key: "milestones", label: "Milestones" },
          { key: "surveys", label: "Surveys" },
          trialsItem,
          ...(isFlagEnabled(FEATURE_FLAGS.PORT_DOCUMENTS)
            ? [{ key: "site-documents", label: "Site Documents" }]
            : []),
          ...(notesHidden ? [] : [{ key: "notes", label: "Notes" }]),
          { key: "mastery", label: "Data Mastery" },
        ],
      },
      ...(isFlagEnabled(FEATURE_FLAGS.SITE_RISK_LIFECYCLE)
        ? [
            {
              type: "group" as const,
              label: "RISK",
              key: "group-risk",
              children: [{ key: "risk", label: "Risk" }],
            },
          ]
        : []),
    ];
  }

  return [
    {
      type: "group",
      label: "SITE OVERVIEW",
      key: "group-site-overview",
      children: [
        trialsItem,
        ...(!gated || isFlagEnabled(FEATURE_FLAGS.SITE_ACTIVATION_MILESTONE)
          ? [{ key: "milestones", label: "Milestones", disabled: noData }]
          : []),
        ...(!gated
          ? [{ key: "surveys", label: "Surveys", disabled: noData }]
          : []),
        ...(!gated
          ? [
              { key: "forms", label: "Forms", disabled: true },
              {
                key: "monitoring-visits",
                label: "Monitoring Visits",
                disabled: true,
              },
            ]
          : []),
        ...(isFlagEnabled(FEATURE_FLAGS.PORT_DOCUMENTS)
          ? [{ key: "site-documents", label: "Site Documents" }]
          : []),
        ...(notesHidden ? [] : [{ key: "notes", label: "Notes" }]),
        { key: "mastery", label: "Data Mastery" },
      ],
    },
    ...(isFlagEnabled(FEATURE_FLAGS.SITE_RISK_LIFECYCLE)
      ? [
          {
            type: "group" as const,
            label: "RISK",
            key: "group-risk",
            children: [{ key: "risk", label: "Risk" }],
          },
        ]
      : []),
  ];
};
